import type { ProjectRecord } from "@/lib/api";
import { DOMAIN_LABEL, type TaskGroup } from "@/lib/overview";
import { Box, Text } from "@okshaun/components";
import { css } from "@styled-system/css";
import { RailSection } from "./rail-section";

interface TasksRailProps {
  groups: TaskGroup[];
  readyCount: number;
  waitingCount: number;
  scoped: ProjectRecord | null;
  onClear: () => void;
  /** Task IDs inside a batch that Hal has not applied yet. */
  pendingIds?: Set<string>;
  /** Number of tasks with a triage label, for the "Triage N" link. */
  triageCount?: number;
}

const triageLink = css({
  textStyle: "mono.xs",
  color: "text.accent.purple",
  textDecoration: "none",
  _hover: { textDecoration: "underline" },
  _focusVisible: { textDecoration: "underline", color: "text.bold" },
});

const DOMAIN_INK = {
  cetec: "domain.cetec",
  mkbd: "domain.mkbd",
  personal: "domain.personal",
} as const;

export function TasksRail({
  groups,
  readyCount,
  waitingCount,
  scoped,
  onClear,
  pendingIds,
  triageCount,
}: TasksRailProps) {
  const projectCount = groups.length;
  return (
    <RailSection
      title="Tasks"
      scope={scoped?.name}
      meta={
        scoped
          ? `${readyCount} ready · ${waitingCount} waiting`
          : `${readyCount} ready · in ${projectCount} project${projectCount === 1 ? "" : "s"}`
      }
      onClear={scoped ? onClear : undefined}
      action={
        triageCount ? (
          <a href="/triage" className={triageLink}>
            Triage {triageCount} →
          </a>
        ) : undefined
      }
      scroll
    >
      {groups.length === 0 && (
        <Text textStyle="mono.sm" color="text.subtlest" p="8">
          {scoped ? "No ready tasks on this project." : "No ready tasks match these filters."}
        </Text>
      )}
      {groups.map(({ project, tasks }) => (
        <Box key={project.slug} display="flex" flexDir="column" gap="4" px="12" py="8">
          {!scoped && (
            <Box display="flex" gap="8" alignItems="baseline">
              <Text textStyle="mono.xs" color={DOMAIN_INK[project.domain]}>
                {DOMAIN_LABEL[project.domain]}
              </Text>
              <Text fontSize="13" fontWeight="semibold" color="text.bold">
                {project.name}
              </Text>
            </Box>
          )}
          {tasks.map((t) => (
            <Box key={t.id} display="flex" gap="8" alignItems="baseline">
              <Text textStyle="mono.xs" color="text.subtlest" aria-hidden="true">
                ○
              </Text>
              <Text fontSize="13" lineHeight="default" color="text">
                {t.text}
                {t.due && (
                  <Text as="span" textStyle="mono.xs" color="text.warning" ml="6">
                    due {t.due.slice(5)}
                  </Text>
                )}
                {pendingIds?.has(t.id) && (
                  <Text
                    as="span"
                    textStyle="mono.xs"
                    color="text.accent.purple"
                    ml="6"
                    title="In a batch Hal has not applied yet"
                  >
                    pending
                  </Text>
                )}
              </Text>
            </Box>
          ))}
        </Box>
      ))}
    </RailSection>
  );
}
